import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Download, Languages, LogOut, X } from 'lucide-react'
import { ThemeToggle } from '@/components/theme/ThemeToggle'
import { LanguageSelect } from '@/components/portal/LanguageSelect'
import { ComingSoonBadge } from '@/components/portal/ComingSoonBadge'
import { AppDownloadBadges } from '@/components/portal/AppDownloadBadges'

interface AccountMenuProps {
  open: boolean
  onClose: () => void
  onLogout: () => void
  name: string
  initials: string
}

function focusable(root: HTMLElement): HTMLElement[] {
  return [
    ...root.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
  ].filter((el) => !el.hasAttribute('disabled') && el.getClientRects().length > 0)
}

/** Account panel: anchored popover from 1024px, bottom sheet below. Same focus contract as PortalModal. */
export function AccountMenu({ open, onClose, onLogout, name, initials }: AccountMenuProps) {
  const { t } = useTranslation()
  const dialogRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return
    const previous = document.activeElement as HTMLElement | null
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const root = dialogRef.current
    if (root) focusable(root)[0]?.focus()

    function onKey(event: KeyboardEvent) {
      if (!root) return
      // The language listbox renders in a portal; let it handle its own keys while it is open.
      if (!root.contains(document.activeElement)) return
      if (event.key === 'Escape') {
        event.preventDefault()
        onCloseRef.current()
        return
      }
      if (event.key !== 'Tab') return
      const nodes = focusable(root)
      if (nodes.length === 0) return
      const first = nodes[0]
      const last = nodes[nodes.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKey, true)
    return () => {
      document.body.style.overflow = prevOverflow
      document.removeEventListener('keydown', onKey, true)
      previous?.focus()
    }
  }, [open])

  if (!open) return null

  return (
    <div className="prod-acct-menu-overlay" onClick={onClose} role="presentation">
      <div
        ref={dialogRef}
        className="prod-acct-menu"
        role="dialog"
        aria-modal="true"
        aria-labelledby="account-menu-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="prod-acct-menu__header">
          <span className="prod-avatar prod-avatar--lg" aria-hidden="true">
            {initials}
          </span>
          <div className="prod-acct-menu__who">
            <p className="prod-acct-menu__eyebrow">{t('common.account')}</p>
            <h2 id="account-menu-title" className="prod-acct-menu__name">
              {name || t('common.accountMenu')}
            </h2>
          </div>
          <button type="button" className="prod-acct-menu__close" onClick={onClose} aria-label={t('common.close')}>
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>

        <div className="prod-acct-menu__group">
          <ThemeToggle variant="menu" className="prod-acct-menu__row" />
          <div className="prod-acct-menu__row prod-acct-menu__row--static">
            <Languages className="size-4" aria-hidden="true" />
            <span className="prod-acct-menu__row-label">{t('common.language')}</span>
            <LanguageSelect variant="panel" className="prod-acct-menu__lang" />
          </div>
        </div>

        <div className="prod-acct-menu__group prod-acct-menu__apps">
          <div className="prod-acct-menu__apps-head">
            <Download className="size-4" aria-hidden="true" />
            <span className="prod-acct-menu__row-label">{t('common.downloadApps')}</span>
            <ComingSoonBadge />
          </div>
          <AppDownloadBadges />
        </div>

        <div className="prod-acct-menu__group">
          <button type="button" className="prod-acct-menu__row prod-acct-menu__logout" onClick={onLogout}>
            <LogOut className="size-4" aria-hidden="true" />
            <span>{t('common.logout')}</span>
          </button>
        </div>
      </div>
    </div>
  )
}
